"use client"

import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { 
  Shield, ShieldCheck, ShieldAlert, Users, AlertTriangle, CheckCircle, 
  XCircle, RefreshCw, Eye, Clock, BarChart3, TrendingUp, TrendingDown,
  UserCheck, UserX, Settings, Award, Target, X, Activity
} from 'lucide-react'
import { VisuallyHidden } from '../ui/visually-hidden.jsx'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Performance optimized Team Security component
export function TeamSecurity() {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedMember, setSelectedMember] = useState(null)
  const [showMemberModal, setShowMemberModal] = useState(false)
  const [filterRisk, setFilterRisk] = useState('all') // all, high, medium, low
  const [error, setError] = useState('')

  // Load security dashboard
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      
      const { data } = await axios.get(`${API}/admin/team/security/dashboard`, { 
        withCredentials: true 
      })
      
      setDashboardData(data)
      
    } catch (err) {
      setError('Failed to load security dashboard')
      console.error('Security dashboard error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  // Filter members by risk level
  const filteredMembers = dashboardData?.member_details?.filter(member => {
    if (filterRisk === 'all') return true
    return member.security_risk_level === filterRisk
  }) || []

  // Get security score color
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-50 border-green-200'
    if (score >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-red-600 bg-red-50 border-red-200'
  }

  // Get metric status color
  const getMetricStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-green-600 bg-green-50 border-green-200'
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'critical': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-muted bg-muted-50 border-muted-200'
    }
  }

  // Get risk level styling
  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high': return 'text-red-600 bg-red-100'
      case 'medium': return 'text-yellow-600 bg-yellow-100'
      case 'low': return 'text-green-600 bg-green-100'
      default: return 'text-muted bg-muted-100'
    }
  }

  const handleMemberClick = async (member) => {
    try {
      const { data } = await axios.get(`${API}/admin/team/security/member/${member.id}`, { 
        withCredentials: true 
      })
      setSelectedMember(data)
      setShowMemberModal(true)
    } catch (err) {
      alert(`Failed to load member security details: ${err.response?.data?.detail || err.message}`)
    }
  }

  if (loading) {
    return (
      <div className="team-security mobile-full-width">
        <div className="text-center py-8">
          <div className="animate-pulse">Loading security dashboard...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="team-security mobile-full-width">
        <div className="error mb-4" role="alert">
          {error}
        </div>
        <button onClick={loadDashboard} className="btn primary">
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    )
  }

  if (!dashboardData) {
    return (
      <div className="team-security mobile-full-width">
        <div className="text-center py-8">
          <Shield size={48} className="mx-auto text-muted mb-4" />
          <p className="text-muted">No security data available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="team-security mobile-full-width">
      {/* Header with security score */}
      <div className="flex-col-mobile items-start justify-between mb-6 mobile-full-width">
        <div className="w-full mobile-full-width">
          <h2 className="text-2xl font-semibold text-primary mb-2">Team Security Dashboard</h2>
          <p className="text-muted mb-4">
            Monitor team security posture, MFA adoption, and compliance metrics
          </p>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto mobile-full-width">
          <button 
            onClick={loadDashboard} 
            className="btn secondary"
            disabled={loading}
            aria-label="Refresh security dashboard"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
            <span className="hidden sm:inline ml-2">Refresh</span>
            <VisuallyHidden>
              {loading ? 'Refreshing security data' : 'Reload security dashboard from server'}
            </VisuallyHidden>
          </button>
        </div>
      </div>

      {/* Security Score Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Main Security Score */}
        <div className="lg:col-span-1">
          <div className={`card p-6 text-center border-2 ${getScoreColor(dashboardData.security_score)}`}>
            <div className="text-4xl font-bold mb-2">
              {dashboardData.security_score}
            </div>
            <div className="text-sm font-medium mb-1">Team Security Score</div>
            <div className="text-xs opacity-75">
              Based on {dashboardData.total_members} team members
            </div>
            {dashboardData.load_time_ms && (
              <div className="text-xs mt-2 opacity-60">
                Loaded in {dashboardData.load_time_ms}ms
              </div>
            )}
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="lg:col-span-2">
          <div className="card p-4">
            <h3 className="font-medium text-primary mb-4">Score Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(dashboardData.score_breakdown || {}).map(([component, weight]) => (
                <div key={component} className="flex items-center justify-between">
                  <span className="text-sm capitalize">
                    {component.replace('_weight', '').replace('_', ' ')}
                  </span>
                  <span className="text-sm font-medium">
                    {weight}% weight
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Security Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {Object.entries(dashboardData.metrics || {}).map(([key, metric]) => {
          const Icon = key === 'mfa_adoption' ? UserCheck : 
                     key === 'active_members' ? Users :
                     key === 'role_coverage' ? Shield :
                     Activity
          
          return (
            <div key={key} className={`card p-4 border ${getMetricStatusColor(metric.status)}`}>
              <div className="flex items-center gap-3 mb-2">
                <Icon size={20} />
                <h4 className="font-medium text-sm">
                  {key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h4>
              </div>
              
              <div className="text-2xl font-bold mb-1">
                {metric.percentage}%
              </div>
              
              <div className="flex items-center justify-between text-xs">
                <span>{metric.enabled_count || metric.active_count || metric.assigned_count} / {metric.total_count}</span>
                <span className="opacity-75">Target: {metric.target}%</span>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-muted-200 rounded-full h-2 mt-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    metric.status === 'good' ? 'bg-green-500' :
                    metric.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(metric.percentage, 100)}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Recommendations */}
      <div className="card p-4 mb-6">
        <h3 className="font-medium text-primary mb-3 flex items-center gap-2">
          <Target size={18} />
          Security Recommendations
        </h3>
        <div className="space-y-2">
          {dashboardData.recommendations?.length > 0 ? (
            dashboardData.recommendations.map((recommendation, index) => (
              <div key={index} className="flex items-start gap-2 text-sm">
                <AlertTriangle size={14} className="text-yellow-600 mt-0.5" />
                <span>{recommendation}</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted italic">No recommendations at this time</p>
          )}
        </div>
      </div>

      {/* Member Security Details */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-primary">Team Member Security</h3>
          <select
            className="input w-48"
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value)}
            aria-label="Filter by risk level"
          >
            <option value="all">All Risk Levels</option>
            <option value="high">High Risk</option>
            <option value="medium">Medium Risk</option>
            <option value="low">Low Risk</option>
          </select>
        </div>

        <div className="space-y-2">
          {filteredMembers.length === 0 ? (
            <p className="text-center py-4 text-muted">
              No members found for selected risk level
            </p>
          ) : (
            filteredMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 hover:bg-muted-50 rounded border border-muted-200 transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-primary truncate">{member.name}</span>
                      <span className={`text-xs px-2 py-1 rounded font-medium ${getRiskLevelColor(member.security_risk_level)}`}>
                        {member.security_risk_level} risk
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted">
                      <span>{member.email}</span>
                      <span>{member.role_name}</span>
                      {member.mfa_enabled ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <UserCheck size={12} />
                          MFA
                        </span>
                      ) : (
                        <span className="text-red-600 flex items-center gap-1">
                          <UserX size={12} />
                          No MFA
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleMemberClick(member)}
                  className="btn secondary text-xs"
                  aria-label={`View security details for ${member.name}`}
                >
                  <Eye size={14} />
                  <span className="hidden sm:inline ml-1">Details</span>
                  <VisuallyHidden>
                    View detailed security information for {member.name}
                  </VisuallyHidden>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Member Security Details Modal */}
      {showMemberModal && selectedMember && (
        <MemberSecurityModal
          memberData={selectedMember}
          onClose={() => {
            setShowMemberModal(false)
            setSelectedMember(null)
          }}
        />
      )}
    </div>
  )
}

// Member Security Details Modal Component
function MemberSecurityModal({ memberData, onClose }) {
  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString()
  }

  const getFactorIcon = (enabled) => {
    return enabled ? CheckCircle : XCircle
  }

  const getFactorColor = (enabled) => {
    return enabled ? 'text-green-600' : 'text-red-600'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-canvas rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-muted-200">
          <h3 className="text-xl font-semibold text-primary">Member Security Details</h3>
          <button 
            onClick={onClose}
            className="icon-btn"
            aria-label="Close member security details"
          >
            <X size={18} aria-hidden="true" />
            <VisuallyHidden>Close member security details modal</VisuallyHidden>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Member Information */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-xl font-medium text-primary">
                {memberData.member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-medium text-primary">{memberData.member.name}</h4>
              <p className="text-muted">{memberData.member.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm bg-primary-100 text-primary px-2 py-1 rounded">
                  {memberData.role?.name || 'No Role'}
                </span>
                {memberData.role && (
                  <span className="text-xs text-muted">
                    Level {memberData.role.hierarchy_level}
                  </span>
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {memberData.security_score}
              </div>
              <div className="text-xs text-muted">Security Score</div>
            </div>
          </div>

          {/* Security Factors */}
          <div className="border-t border-muted-200 pt-4">
            <h5 className="text-sm font-medium text-primary mb-3">Security Factors</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(memberData.security_factors || {}).map(([factor, enabled]) => {
                const Icon = getFactorIcon(enabled)
                return (
                  <div key={factor} className={`flex items-center gap-2 p-3 rounded border ${getFactorColor(enabled)} bg-opacity-10`}>
                    <Icon size={16} className={getFactorColor(enabled)} />
                    <span className="text-sm font-medium">
                      {factor.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <span className={`text-xs ml-auto ${getFactorColor(enabled)}`}>
                      {enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Recommendations */}
          <div className="border-t border-muted-200 pt-4">
            <h5 className="text-sm font-medium text-primary mb-3">Recommendations</h5>
            <div className="space-y-2">
              {memberData.recommendations?.map((recommendation, index) => (
                <div key={index} className="flex items-start gap-2 text-sm">
                  {recommendation.includes('✓') ? (
                    <CheckCircle size={14} className="text-green-600 mt-0.5" />
                  ) : (
                    <AlertTriangle size={14} className="text-yellow-600 mt-0.5" />
                  )}
                  <span>{recommendation}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Audit Activity */}
          {memberData.recent_audit && memberData.recent_audit.length > 0 && (
            <div className="border-t border-muted-200 pt-4">
              <h5 className="text-sm font-medium text-primary mb-3">Recent Activity</h5>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {memberData.recent_audit.slice(0, 5).map((entry) => (
                  <div key={entry.id} className="flex items-center gap-3 p-2 bg-muted-50 rounded text-sm">
                    <Clock size={12} className="text-muted" />
                    <div className="flex-1">
                      <span className="font-medium">{entry.action.replace('_', ' ')}</span>
                      <span className="text-muted ml-2">
                        {formatDate(entry.timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Close Actions */}
          <div className="border-t border-muted-200 pt-4">
            <div className="flex justify-end">
              <button 
                onClick={onClose}
                className="btn secondary"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamSecurity